import { FC, useEffect } from "react";
import { useState } from "react";
import { Box, IconButton } from "@mui/material";

import { styles } from "../stylesHome";
import CustomFilter from "@/components/UI/CustomFilter/CustomFilter";
import RefreshSvg from "@/assets/svg/RefreshSvg";
import DraftSvg from "@/assets/svg/DraftSvg";
import { useGetDraftsCountQuery } from "@/store/api/draftsApi";
import { useNavigate } from "react-router-dom";
import { allTimeOptions, PREMIUM_TIME_OPTIONS } from "@/types/timeRanges";
import theme from "@/theme";

interface IFilterProps {
  setParams: (params: any) => void;
  sortDirection: "asc" | "desc";
  setSortDirection: (val: "asc" | "desc") => void;
  onRefreshPosts: () => void;
  limit?: number;
  isPremium?: boolean;
  initialTimeRange?: string;
  initialWho?: string;
  initialCategory?: string;
  updateUrlParams?: (newParams: Record<string, string>) => void;
}

const Filter: FC<IFilterProps> = ({
  setParams,
  sortDirection,
  setSortDirection,
  onRefreshPosts,
  limit = 100,
  isPremium = false,
  initialTimeRange,
  initialWho,
  updateUrlParams,
  initialCategory,
}) => {
  const paramsQuery = new URLSearchParams(location.search);
  const orderTypeFromUrl = paramsQuery.get("orderType") || "New";
  const [sortField, setSortField] = useState(() => {
    return {
      new: orderTypeFromUrl === "New",
      top: orderTypeFromUrl === "Top",
      day: initialTimeRange || "All",
      who: initialWho || "",
      category: initialCategory || "",
    };
  });
  useEffect(() => {
    const foundTime = allTimeOptions.find(
      (t) => t.label === initialTimeRange || t.value === initialTimeRange
    );
    setSortField((prev) => ({
      ...prev,
      day: foundTime?.label || "All",
      who: initialWho || "",
    }));
  }, [initialTimeRange, initialWho]);
  const { data: draftData } = useGetDraftsCountQuery();
  const draftCount = draftData?.count || 0;
  const navigate = useNavigate();
  const handleSelect = (field: keyof typeof sortField, value: string | boolean) => {
    setSortField((prev: typeof sortField) => ({ ...prev, [field]: value }));
  };
  useEffect(() => {
    setSortField({
      new: orderTypeFromUrl === "New",
      top: orderTypeFromUrl === "Top",
      day: initialTimeRange || "All",
      who: initialWho || "",
      category: initialCategory || "",
    });
  }, [initialTimeRange, initialWho, orderTypeFromUrl]);

  useEffect(() => {
    let topic = "";
    let subtype = "";

    if (sortField.category.startsWith("Stocks")) {
      topic = "STOCKS";
      if (sortField.category === "Stocks - Technical") subtype = "TECHNICAL";
      else if (sortField.category === "Stocks - Fundamental") subtype = "FUNDAMENTAL";
    } else if (sortField.category === "Economics") {
      topic = "ECONOMICS";
    } else if (sortField.category === "Personal") {
      topic = "PERSONAL";
    }

    const orderType = sortField.new ? "New" : sortField.top ? "Top" : "New";

    const updatedParams = {
      timeRange: allTimeOptions.find((opt) => opt.label === sortField.day)?.value || "",
      who: sortField.who ? sortField.who.toLowerCase() : "",
      orderType,
    };

    setParams({
      page: 1,
      limit,
      text: "",
      author: "",
      cashtag: "",
      hashtag: "",
      who: updatedParams.who,
      timeRange: updatedParams.timeRange,
      orderType: updatedParams.orderType,
      topic,
      subtype,
      sortBy: sortField.top ? "likes" : "createdAt",
    });

    updateUrlParams && updateUrlParams(updatedParams);
  }, [sortField, sortDirection]);

  return (
    <Box>
      <Box sx={styles.filterWrapper}>
        <CustomFilter
          label="New"
          isActive={sortField.new}
          onSelect={() => {
            handleSelect("new", !sortField.new);
            handleSelect("top", false);
            if (!sortField.new) setSortDirection("desc");
          }}
          width={67}
        />

        <CustomFilter
          label="Top"
          isActive={sortField.top}
          onSelect={() => {
            handleSelect("top", !sortField.top);
            handleSelect("new", false);
          }}
          width={61}
        />

        <CustomFilter
          label={sortField.day || "All"}
          isDropdown
          isActive={sortField.day !== "All"}
          options={allTimeOptions.map((o) => o.label)}
          disabledOptions={!isPremium ? PREMIUM_TIME_OPTIONS.map((o) => o.label) : []}
          onSelect={(value) => handleSelect("day", value)}
        />

        <CustomFilter
          label={sortField.who || "Who"}
          isDropdown
          isActive={!!sortField.who}
          options={["All", "Top", "Following"]}
          disabledOptions={!isPremium ? ["Following"] : []}
          onSelect={(value) => handleSelect("who", value)}
        />

        <CustomFilter
          label={sortField.category || "Category"}
          isDropdown
          isActive={!!sortField.category}
          options={["All", "Stocks", "Economics", "Personal"]}
          expandableOptions={{
            Stocks: [
              { key: "Stocks - All", label: "All" },
              { key: "Stocks - Technical", label: "Technical" },
              { key: "Stocks - Fundamental", label: "Fundamental" },
            ],
          }}
          onSelect={(value) => handleSelect("category", value)}
          selectedValue={sortField.category}
        />
        <Box sx={styles.iconButtons}>
          <IconButton sx={{ padding: 0 }} size="small" onClick={onRefreshPosts}>
            <RefreshSvg width={"22px"} height={"22px"} />
          </IconButton>
        </Box>
      </Box>

      {draftCount > 0 && (
        <Box
          sx={{
            width: "100%",
            height: "46px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "261px",
            padding: "4px 20px",
            boxSizing: "border-box",
            background: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "38px",
              borderRadius: "22px",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <DraftSvg />
            <Box
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: theme.palette.custom.mainBlue,
                whiteSpace: "nowrap",
              }}
              onClick={() => navigate("/drafts")}
            >
              You have unpublished drafts: {draftCount}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Filter;
